// Sample data generator for rooms
const generateSampleRooms = () => {
  const roomTypes = [
    { name: 'Single Room', base_price: 80, max_occupancy: 1 },
    { name: 'Double Room', base_price: 120, max_occupancy: 2 },
    { name: 'Deluxe Room', base_price: 180, max_occupancy: 3 },
    { name: 'Suite', base_price: 300, max_occupancy: 4 },
    { name: 'Studio Apartment', base_price: 150, max_occupancy: 2 },
    { name: 'One Bedroom Apartment', base_price: 250, max_occupancy: 4 }
  ]

  const amenities = ['WiFi', 'AC', 'TV', 'Mini Bar', 'Safe', 'Balcony', 'Kitchen', 'Washing Machine']
  const statuses = ['available', 'occupied', 'maintenance', 'reserved']

  const rooms = []
  let roomId = 1

  // Generate rooms for each floor (4 floors)
  for (let floor = 1; floor <= 4; floor++) {
    // Generate 10 rooms per floor
    for (let roomNum = 1; roomNum <= 10; roomNum++) {
      const roomType = roomTypes[Math.floor(Math.random() * roomTypes.length)]
      const selectedAmenities = amenities
        .filter(() => Math.random() > 0.5)
        .slice(0, Math.floor(Math.random() * 5) + 3)

      rooms.push({
        id: roomId++,
        room_number: `${floor}${String(roomNum).padStart(2, '0')}`,
        room_type: roomType.name,
        floor: floor,
        base_price: roomType.base_price,
        max_occupancy: roomType.max_occupancy,
        amenities: selectedAmenities,
        status: statuses[Math.floor(Math.random() * statuses.length)],
        housekeeping_status: ['clean', 'cleaning', 'dirty'][Math.floor(Math.random() * 3)],
        last_cleaned: new Date(Date.now() - Math.random() * 48 * 60 * 60 * 1000).toISOString(),
        description: `Comfortable ${roomType.name.toLowerCase()} with modern amenities`,
        images: [`/images/rooms/${roomType.name.toLowerCase().replace(' ', '-')}-1.jpg`]
      })
    }
  }

  return rooms
}
export default class CategoryService {
  constructor (http, store, router) {
    this.$http = http
    this.$store = store
    this.$router = router
  }

  // Get all rooms with filters
  getRooms (params = {}) {
    if (process.env.VUE_APP_DEMO_MODE) {
      let rooms = generateSampleRooms()

      // Apply filters
      if (params.status) {
        rooms = rooms.filter(r => r.status === params.status)
      }
      if (params.room_type) {
        rooms = rooms.filter(r => r.room_type === params.room_type)
      }
      if (params.floor) {
        rooms = rooms.filter(r => r.floor === parseInt(params.floor))
      }

      return Promise.resolve({
        data: {
          data: rooms,
          total: rooms.length,
          current_page: 1,
          per_page: 50
        }
      })
    }

    return this.$http.get('/rooms', { params })
  }

  // Get single room details
  getRoom (id) {
    if (process.env.VUE_APP_DEMO_MODE) {
      const rooms = generateSampleRooms()
      const room = rooms.find(r => r.id === parseInt(id))
      return Promise.resolve({ data: room })
    }

    return this.$http.get(`/rooms/${id}`)
  }

  // Create new room
  createRoom (data) {
    if (process.env.VUE_APP_DEMO_MODE) {
      return Promise.resolve({
        data: {
          ...data,
          id: Date.now(),
          status: 'available',
          housekeeping_status: 'clean',
          created_at: new Date().toISOString()
        }
      })
    }

    return this.$http.post('/rooms', data)
  }

  // Update room
  updateRoom (id, data) {
    if (process.env.VUE_APP_DEMO_MODE) {
      return Promise.resolve({ data: { ...data, id } })
    }

    return this.$http.put(`/rooms/${id}`, data)
  }

  // Delete room
  deleteRoom (id) {
    if (process.env.VUE_APP_DEMO_MODE) {
      return Promise.resolve({ data: { message: 'Room deleted successfully' } })
    }

    return this.$http.delete(`/rooms/${id}`)
  }

  // Update room status
  updateRoomStatus (id, status) {
    if (process.env.VUE_APP_DEMO_MODE) {
      return Promise.resolve({
        data: {
          id,
          status,
          updated_at: new Date().toISOString()
        }
      })
    }

    return this.$http.patch(`/rooms/${id}/status`, { status })
  }

  // Update housekeeping status
  updateHousekeepingStatus (id, status) {
    if (process.env.VUE_APP_DEMO_MODE) {
      return Promise.resolve({
        data: {
          id,
          housekeeping_status: status,
          last_cleaned: status === 'clean' ? new Date().toISOString() : null
        }
      })
    }

    return this.$http.patch(`/rooms/${id}/housekeeping`, { status })
  }

  // Get room types
  getRoomTypes () {
    if (process.env.VUE_APP_DEMO_MODE) {
      return Promise.resolve({
        data: [
          { id: 1, name: 'Single Room', base_price: 80, max_occupancy: 1 },
          { id: 2, name: 'Double Room', base_price: 120, max_occupancy: 2 },
          { id: 3, name: 'Deluxe Room', base_price: 180, max_occupancy: 3 },
          { id: 4, name: 'Suite', base_price: 300, max_occupancy: 4 },
          { id: 5, name: 'Studio Apartment', base_price: 150, max_occupancy: 2 },
          { id: 6, name: 'One Bedroom Apartment', base_price: 250, max_occupancy: 4 }
        ]
      })
    }

    return this.$http.get('/room-types')
  }

  // Get room amenities list
  getAmenities () {
    if (process.env.VUE_APP_DEMO_MODE) {
      return Promise.resolve({
        data: [
          { id: 1, name: 'WiFi', icon: 'mdi-wifi' },
          { id: 2, name: 'AC', icon: 'mdi-air-conditioner' },
          { id: 3, name: 'TV', icon: 'mdi-television' },
          { id: 4, name: 'Mini Bar', icon: 'mdi-fridge' },
          { id: 5, name: 'Safe', icon: 'mdi-safe' },
          { id: 6, name: 'Balcony', icon: 'mdi-balcony' },
          { id: 7, name: 'Kitchen', icon: 'mdi-silverware-fork-knife' },
          { id: 8, name: 'Washing Machine', icon: 'mdi-washing-machine' }
        ]
      })
    }

    return this.$http.get('/amenities')
  }

  // Get room availability for date range
  checkRoomAvailability (roomId, startDate, endDate) {
    if (process.env.VUE_APP_DEMO_MODE) {
      return Promise.resolve({
        data: {
          room_id: roomId,
          available: Math.random() > 0.3,
          bookings: Math.random() > 0.5
            ? [
                {
                  check_in: startDate,
                  check_out: endDate,
                  guest_name: 'John Doe'
                }
              ]
            : []
        }
      })
    }

    return this.$http.get(`/rooms/${roomId}/availability`, {
      params: { start_date: startDate, end_date: endDate }
    })
  }

  // Get room statistics
  getRoomStats () {
    if (process.env.VUE_APP_DEMO_MODE) {
      return Promise.resolve({
        data: {
          total_rooms: 40,
          available_rooms: 15,
          occupied_rooms: 20,
          maintenance_rooms: 3,
          reserved_rooms: 2,
          occupancy_rate: 50,
          average_daily_rate: 145.50
        }
      })
    }

    return this.$http.get('/rooms/stats')
  }

  // Get maintenance history
  getMaintenanceHistory (roomId) {
    if (process.env.VUE_APP_DEMO_MODE) {
      return Promise.resolve({
        data: [
          {
            id: 1,
            room_id: roomId,
            type: 'Regular Maintenance',
            description: 'AC servicing and general inspection',
            date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
            completed: true
          },
          {
            id: 2,
            room_id: roomId,
            type: 'Repair',
            description: 'Fixed bathroom plumbing',
            date: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
            completed: true
          }
        ]
      })
    }

    return this.$http.get(`/rooms/${roomId}/maintenance`)
  }

  // Schedule maintenance
  scheduleMaintenance (roomId, data) {
    if (process.env.VUE_APP_DEMO_MODE) {
      return Promise.resolve({
        data: {
          id: Date.now(),
          room_id: roomId,
          ...data,
          scheduled_date: data.scheduled_date,
          status: 'scheduled'
        }
      })
    }

    return this.$http.post(`/rooms/${roomId}/maintenance`, data)
  }
}
